import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Calendar, AlertCircle, Building2, RefreshCw, UserPlus, X } from 'lucide-react';
import api, { uploadRequestConfig } from '../../services/api';
import FileUploader from '../common/FileUploader';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';
import { ORGANIZATION_OPTIONS } from '../../constants/organizations';
import { calculateTaskDueDate, formatDateForDisplay, formatDateForInput } from '../../utils/taskDueDateCalculator';

/** Distinct value for employees with no organization set (matches filter logic). */
const NO_ORGANIZATION_VALUE = '__no_organization__';

const CreateTask = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: [],
    priority: 'medium',
    cadence: 'weekly',
    dueDate: formatDateForInput(calculateTaskDueDate('weekly'))
  });
  const [files, setFiles] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const { organizationNames, hasEmployeesWithoutOrg } = useMemo(() => {
    const names = new Set(ORGANIZATION_OPTIONS);
    let withoutOrg = false;
    employees.forEach((e) => {
      const org = (e.organization || '').trim();
      if (org) names.add(org);
      else withoutOrg = true;
    });
    return {
      organizationNames: Array.from(names).sort((a, b) => a.localeCompare(b)),
      hasEmployeesWithoutOrg: withoutOrg,
    };
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (!selectedOrganization) return [];
    if (selectedOrganization === NO_ORGANIZATION_VALUE) {
      return employees.filter((e) => !(e.organization || '').trim());
    }
    return employees.filter(
      (e) => (e.organization || '').trim() === selectedOrganization
    );
  }, [employees, selectedOrganization]);

  const employeeById = useMemo(() => {
    const map = new Map();
    employees.forEach((e) => {
      const id = getUserIdentifier(e);
      if (id) map.set(id, e);
    });
    return map;
  }, [employees]);

  const availableToAdd = useMemo(() => {
    return filteredEmployees
      .filter((e) => {
        const id = getUserIdentifier(e);
        return id && !formData.assignedTo.includes(id);
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [filteredEmployees, formData.assignedTo]);

  const handleOrganizationSelect = (value) => {
    setSelectedOrganization(value);
    setFormData((prev) => ({ ...prev, assignedTo: [] }));
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees');
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch employees');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };
    
    // Auto-calculate due date when cadence changes
    if (name === 'cadence') {
      const calculatedDueDate = calculateTaskDueDate(value);
      updatedData.dueDate = formatDateForInput(calculatedDueDate);
    }
    
    setFormData(updatedData);
  };

  const addAssignee = (employeeId) => {
    if (!employeeId) return;
    setFormData((prev) =>
      prev.assignedTo.includes(employeeId)
        ? prev
        : { ...prev, assignedTo: [...prev.assignedTo, employeeId] }
    );
  };

  const removeAssignee = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.filter((id) => id !== employeeId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a task description');
      return;
    }
    if (!selectedOrganization) {
      toast.error('Please select a company');
      return;
    }
    if (formData.assignedTo.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      setLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('title', formData.title.trim());
      submitData.append('description', formData.description.trim());
      submitData.append('assignedTo', JSON.stringify(formData.assignedTo));
      submitData.append('priority', formData.priority);
      submitData.append('cadence', formData.cadence);
      
      if (formData.dueDate) {
        submitData.append('dueDate', new Date(formData.dueDate).toISOString());
      }

      // Append files
      files.forEach(file => {
        submitData.append('attachments', file);
      });

      await api.post('/admin/tasks', submitData, uploadRequestConfig());

      toast.success('Task created successfully!');
      navigate('/admin/tasks');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = formData.assignedTo.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/tasks')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Task</h1>
          <p className="text-gray-600 mt-1">Assign a new task to your team members</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company + assignees first: same GET /admin/employees; filter by employee.organization */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                <Users className="inline h-5 w-5 mr-2" />
                Assign to Employees
                <span className="text-red-500 ml-1">*</span>
              </h3>
              {selectedCount > 0 && (
                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                  {selectedCount} selected
                </span>
              )}
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="inline h-4 w-4 mr-1" />
                Company <span className="text-red-500">*</span>
              </label>
              <select
                id="organization"
                value={selectedOrganization}
                onChange={(e) => handleOrganizationSelect(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select company</option>
                {organizationNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                {hasEmployeesWithoutOrg && (
                  <option value={NO_ORGANIZATION_VALUE}>No organization</option>
                )}
              </select>
              <p className="mt-1.5 text-xs text-gray-500">
                Pick people from the dropdown; remove anyone with the × on their tag.
              </p>
            </div>

            {selectedOrganization ? (
              filteredEmployees.length > 0 ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="add-assignee" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                      <UserPlus className="h-4 w-4 text-gray-500" />
                      Add assignee
                    </label>
                    <select
                      id="add-assignee"
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) addAssignee(v);
                      }}
                      disabled={availableToAdd.length === 0}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">
                        {availableToAdd.length === 0
                          ? formData.assignedTo.length > 0
                            ? 'All employees in this company are assigned'
                            : 'No employees to add'
                          : 'Choose an employee to add…'}
                      </option>
                      {availableToAdd.map((employee) => {
                        const employeeKey = getUserIdentifier(employee);
                        if (!employeeKey) return null;
                        const label = [employee.name || 'Unnamed', employee.employeeId || employee.email]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <option key={employeeKey} value={employeeKey}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {formData.assignedTo.length > 0 ? (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Assigned to</p>
                      <ul className="flex flex-wrap gap-2">
                        {formData.assignedTo.map((id) => {
                          const emp = employeeById.get(id);
                          const initial = (emp?.name || '?').charAt(0).toUpperCase();
                          return (
                            <li
                              key={id}
                              className="inline-flex items-center gap-2 pl-1 pr-1 py-1 rounded-full border border-primary-200 bg-primary-50 text-sm text-gray-900 max-w-full"
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                                {initial}
                              </span>
                              <span className="truncate min-w-0 pr-1">
                                <span className="font-medium">{emp?.name || 'Employee'}</span>
                                {(emp?.employeeId || emp?.email) && (
                                  <span className="text-gray-500 font-normal">
                                    {' '}
                                    · {emp?.employeeId || emp?.email}
                                  </span>
                                )}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeAssignee(id)}
                                className="shrink-0 p-1 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                aria-label={`Remove ${emp?.name || 'assignee'}`}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
                      No one assigned yet — use the dropdown above.
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 rounded-lg border border-dashed border-gray-200 bg-gray-50">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <p className="text-gray-600">No employees for this company</p>
                </div>
              )
            ) : (
              <div className="text-center py-6 rounded-lg bg-slate-50 text-slate-600 text-sm">
                Select a company to load employees.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Task Details</h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Task Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter a clear and concise task title..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Task Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Task Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                required
                value={formData.description}
                onChange={handleChange}
                rows={6}
                placeholder="Provide detailed information about the task, requirements, and expected outcomes..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Priority, task frequency, and due date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Priority */}
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Task Frequency/Target - dropdown only */}
              <div>
                <label htmlFor="cadence" className="block text-sm font-medium text-gray-700 mb-2">
                  <RefreshCw className="inline h-4 w-4 mr-1" />
                  Task Target <span className="text-red-500">*</span>
                </label>
                <select
                  id="cadence"
                  name="cadence"
                  value={formData.cadence}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (by Saturday)</option>
                  <option value="monthly">Monthly (end of month)</option>
                  <option value="yearly">Yearly (end of year)</option>
                </select>
              </div>
            </div>

            {/* Auto-Calculated Due Date Display */}
            {formData.dueDate && (
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Auto-Calculated Due Date</p>
                    <p className="text-lg font-semibold text-primary-700 mt-1">
                      {formatDateForDisplay(new Date(formData.dueDate))}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Based on "{formData.cadence.charAt(0).toUpperCase() + formData.cadence.slice(1)}" task frequency
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments (Optional)
              </label>
              <FileUploader
                files={files}
                onFilesChange={setFiles}
                maxFiles={5}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/tasks')}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedOrganization || formData.assignedTo.length === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTask;
